import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Scenario } from 'api';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import { CapTableVersionWithCompanyId, ChildCompany } from 'common/types/company';
import { Format } from 'common/types/format';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { ScalarSpreadsheetProps } from 'components/ScalarSpreadsheet/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import { useWaterfallContext } from 'pages/Waterfall/data/WaterfallContext/WaterfallContext';
import useFundInvestmentSheetConfig from 'pages/Waterfall/SpreadsheetConfig/FundInvestmentSheetConfig/useFundInvestmentSheetConfig/useFundInvestmentSheetConfig';
import { createRelatedEntityInputSheetConfig } from 'pages/Waterfall/SpreadsheetConfig/RelatedEntityInputSheetConfig/relatedEntityInputSheetConfig';
import useWaterfallSheetConfig, {
  createUnavailableWaterfallSheetConfig,
} from 'pages/Waterfall/SpreadsheetConfig/WaterfallSheetConfig/useWaterfallSheetConfig/useWaterfallSheetConfig';
import { RelatedEntitySheet } from 'pages/Waterfall/utilities/useRelatedEntitiesWaterfalls';
import { WeightedShareValue } from '../FundFirmDistribution/FundFirmDistribution.props';
import WaterfallRecalculation from '../WaterfallRecalculation';
import WaterfallTableFormatter from '../WaterfallTableFormatter';
import {
  INVALID_WATERFALL_OVERLAY_STYLES,
  INVALID_WATERFALL_RESULTS_STYLES,
} from './RelatedEntityWaterfallSheets.styles';
import WaterfallTables from '../WaterfallTables/WaterfallTables';

const WATERFALL_RELATED_ENTITY_INPUT_CONTAINER_CLASS = `${WATERFALL_SLUG}-related-entity-input-container`;

const RelatedEntityWaterfallSheets = ({
  relatedEntity,
  scenarioResult,
  childrenCapTables,
  ownedSharesValue,
  registerChildrenSheets,
  inputSheet,
  inputFormat,
  isLoading,
  equityValue,
  onInputChange,
}: {
  relatedEntity: ChildCompany;
  scenarioResult?: Scenario;
  childrenCapTables: CapTableVersionWithCompanyId[];
  ownedSharesValue?: WeightedShareValue[];
  registerChildrenSheets: (sheets: RelatedEntitySheet[], idKey: number) => void;
  inputSheet: ReturnType<typeof createRelatedEntityInputSheetConfig>;
  inputFormat: Format;
  isLoading: boolean;
  equityValue?: number;
  onInputChange: (cell: EvaluatedCell, value: unknown) => void;
}) => {
  const { formatDispatch } = useWaterfallContext();
  const [hasCompletedRequestWithoutResults, setHasCompletedRequestWithoutResults] = useState(false);
  const wasLoadingRef = useRef(isLoading);
  const validSecurities = useMemo(
    () => childrenCapTables.find(ct => Number(ct.company_id) === Number(relatedEntity.company_id))?.securities ?? [],
    [childrenCapTables, relatedEntity.company_id]
  );
  const { sheetConfig: waterfallSheet } = useWaterfallSheetConfig({
    scenarioObject: scenarioResult,
    validSecurities,
    companyName: relatedEntity.company_name,
    relatedEntityId: Number(relatedEntity.company_id),
  });
  const { spreadsheets: fundInvestmentSheets } = useFundInvestmentSheetConfig({
    weightedShareValues: ownedSharesValue,
    validSecurities,
    idToAssociateTo: Number(relatedEntity.company_id),
  });
  const [isResultsPresentationReady, setIsResultsPresentationReady] = useState(
    () => !isLoading && Boolean(waterfallSheet)
  );
  const hasNegativeEquityValue = Number.isFinite(equityValue) && Number(equityValue) < 0;
  const lastSettledResultsRef = useRef<
    | {
        waterfallSheet: NonNullable<typeof waterfallSheet>;
        fundInvestmentSheets: typeof fundInvestmentSheets;
      }
    | undefined
  >(undefined);
  const needsUnavailableLayout = hasNegativeEquityValue && !lastSettledResultsRef.current;
  const unavailableSheets = useMemo(
    () => needsUnavailableLayout
      ? [createUnavailableWaterfallSheetConfig({
        validSecurities,
        companyName: relatedEntity.company_name,
        relatedEntityId: Number(relatedEntity.company_id),
      })]
      : [],
    [needsUnavailableLayout, relatedEntity.company_id, relatedEntity.company_name, validSecurities]
  );
  // Evaluate the blank presentation separately: it must never enter the parent's
  // workbook or contribute placeholder values to Firm Total calculations.
  const { data: unavailableWorkbookData } = useWorkbook(unavailableSheets);
  const unavailableSheet = unavailableSheets[0] &&
    unavailableWorkbookData?.[unavailableSheets[0].name] === unavailableSheets[0]
    ? unavailableSheets[0]
    : undefined;

  const sheets = useMemo(
    () => [waterfallSheet, ...fundInvestmentSheets].filter(sheet => !!sheet),
    [waterfallSheet, fundInvestmentSheets]
  );

  // Register the child configs in the parent workbook for cross-sheet fund totals.
  useEffect(() => {
    if (sheets.length) {
      registerChildrenSheets(sheets, Number(relatedEntity.company_id));
    }
  }, [sheets, registerChildrenSheets, relatedEntity.company_id]);

  // Keep a fully presented layout through edits, including recovery from an
  // initially invalid URL that has never produced calculated results.
  useEffect(() => {
    if (waterfallSheet && !isLoading && isResultsPresentationReady && !hasNegativeEquityValue) {
      lastSettledResultsRef.current = { waterfallSheet, fundInvestmentSheets };
    } else if (hasNegativeEquityValue && unavailableSheet && !lastSettledResultsRef.current) {
      lastSettledResultsRef.current = { waterfallSheet: unavailableSheet, fundInvestmentSheets: [] };
    }
  }, [fundInvestmentSheets, hasNegativeEquityValue, isLoading, isResultsPresentationReady, unavailableSheet, waterfallSheet]);

  // Let workbook evaluation and spreadsheet measurement finish before revealing new values.
  useEffect(() => {
    if (isLoading || !waterfallSheet) {
      setIsResultsPresentationReady(false);
      return undefined;
    }

    let secondAnimationFrame: number | undefined;
    const firstAnimationFrame = window.requestAnimationFrame(() => {
      secondAnimationFrame = window.requestAnimationFrame(() => {
        setIsResultsPresentationReady(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstAnimationFrame);
      if (secondAnimationFrame !== undefined) {
        window.cancelAnimationFrame(secondAnimationFrame);
      }
    };
  }, [isLoading, waterfallSheet]);

  // Distinguish a settled empty response from the brief gap before a new request starts.
  useEffect(() => {
    if (waterfallSheet || isLoading || hasNegativeEquityValue) {
      setHasCompletedRequestWithoutResults(false);
    } else if (wasLoadingRef.current) {
      setHasCompletedRequestWithoutResults(true);
    }
    wasLoadingRef.current = isLoading;
  }, [hasNegativeEquityValue, isLoading, waterfallSheet]);

  const isAwaitingResultsPresentation =
    !hasNegativeEquityValue && (isLoading || Boolean(waterfallSheet && !isResultsPresentationReady));
  const settledResults = useMemo(
    () => {
      if (hasNegativeEquityValue) {
        return lastSettledResultsRef.current ?? (
          unavailableSheet ? { waterfallSheet: unavailableSheet, fundInvestmentSheets: [] } : undefined
        );
      }
      return waterfallSheet && !isLoading && isResultsPresentationReady
        ? { waterfallSheet, fundInvestmentSheets }
        : lastSettledResultsRef.current;
    },
    [fundInvestmentSheets, hasNegativeEquityValue, isLoading, isResultsPresentationReady, unavailableSheet, waterfallSheet]
  );
  // A valid edit becomes observable one render before the request loading flag.
  // Keep the last settled geometry through that gap so the page never collapses.
  const isHoldingResultsSpace =
    Boolean(settledResults) &&
    (hasNegativeEquityValue ||
      isAwaitingResultsPresentation ||
      (!waterfallSheet && !hasCompletedRequestWithoutResults));
  const unavailableResultsMessage = (
    <>
      <Typography color="error" fontWeight={700} variant="body1">
        Unable to calculate waterfall
      </Typography>
      <Typography color="error" fontWeight={600} variant="body2">
        Exit Equity Value cannot be negative.
      </Typography>
    </>
  );

  return (
    <Box
      aria-busy={isAwaitingResultsPresentation || undefined}
      data-testid={`related-entity-waterfall-${relatedEntity.company_id}`}>
      <Box
        className={WATERFALL_RELATED_ENTITY_INPUT_CONTAINER_CLASS}
        data-testid={`related-entity-input-${relatedEntity.company_id}`}
        sx={{ display: 'inline-block' }}>
        <WaterfallTableFormatter />
        <ScalarSpreadsheet
          {...inputSheet}
          data={inputSheet.data ?? []}
          totalData={inputSheet.totalData ?? []}
          titleData={inputSheet.titleData ?? []}
          disableUnsavedChanges
          disableVerticalScroll
          format={inputFormat}
          formatDispatch={formatDispatch}
          onChange={onInputChange}
          selectionSummaryUsesCellCurrencyFormat
          sheet={inputSheet as unknown as ScalarSpreadsheetProps['sheet']}
          showToolbar
        />
      </Box>
      {settledResults && (waterfallSheet || isHoldingResultsSpace) ? (
        <Box
          aria-hidden={(isHoldingResultsSpace && !hasNegativeEquityValue) || undefined}
          data-testid={`related-entity-results-${relatedEntity.company_id}`}
          // Size the status overlay to the result tables, not the surrounding page.
          sx={{ position: 'relative', width: 'fit-content', maxWidth: '100%' }}>
          <WaterfallRecalculation
            aria-hidden={isHoldingResultsSpace || undefined}
            data-testid={`related-entity-results-content-${relatedEntity.company_id}`}
            inert={isHoldingResultsSpace || undefined}
            isRecalculating={isHoldingResultsSpace && !hasNegativeEquityValue}
            sx={hasNegativeEquityValue ? INVALID_WATERFALL_RESULTS_STYLES : undefined}>
            <Box
              data-testid={
                isHoldingResultsSpace && !hasNegativeEquityValue
                  ? `related-entity-results-placeholder-${relatedEntity.company_id}`
                  : undefined
              }>
              <WaterfallTables
                waterfallSheet={settledResults.waterfallSheet}
                fundInvestmentsSheets={settledResults.fundInvestmentSheets}
              />
            </Box>
          </WaterfallRecalculation>
          {hasNegativeEquityValue && (
            <Box role="status" sx={INVALID_WATERFALL_OVERLAY_STYLES}>
              {unavailableResultsMessage}
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default RelatedEntityWaterfallSheets;
